/**
 * Sync engine for offline-first markers.
 * Pushes pending local markers to Supabase and updates sync status.
 */

import { supabase } from "@/integrations/supabase/client";
import {
  getLocalMarkers,
  getPendingMarkers,
  updateSyncStatus,
  getAllMarkerContentIds,
} from "./markerLocalStorage";
import type { LocalMarker } from "./markerLocalStorage";

/** Create a new marker in Supabase */
async function syncCreateMarker(marker: LocalMarker, videoContentId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("recording_markers" as any)
    .insert({
      video_content_id: marker.video_content_id,
      shooting_id: marker.shooting_id,
      studio_id: marker.studio_id,
      timecode: marker.timecode,
      elapsed_ms: marker.elapsed_ms,
      marker_type: marker.marker_type,
      label: marker.label,
      color: marker.color,
      created_by: marker.created_by,
    } as any)
    .select()
    .single();

  if (error) {
    console.warn("[markerSync] create failed:", error.message);
    return false;
  }

  updateSyncStatus(videoContentId, marker._localId, "synced", (data as any).id);
  return true;
}

/** Update an existing marker in Supabase */
async function syncUpdateMarker(marker: LocalMarker, videoContentId: string): Promise<boolean> {
  const { error } = await supabase
    .from("recording_markers" as any)
    .update({
      label: marker.label,
      color: marker.color,
      timecode: marker.timecode,
      marker_type: marker.marker_type,
    } as any)
    .eq("id", marker.id);

  if (error) {
    console.warn("[markerSync] update failed:", error.message);
    return false;
  }

  updateSyncStatus(videoContentId, marker._localId, "synced");
  return true;
}

/** Delete a marker from Supabase and local storage */
async function syncDeleteMarker(marker: LocalMarker, videoContentId: string): Promise<boolean> {
  const { error } = await supabase
    .from("recording_markers" as any)
    .delete()
    .eq("id", marker.id);

  if (error) {
    console.warn("[markerSync] delete failed:", error.message);
    return false;
  }

  // Remove from local storage entirely
  const all = getLocalMarkers(videoContentId);
  const filtered = all.filter((m) => m._localId !== marker._localId);
  localStorage.setItem(`markers_${videoContentId}`, JSON.stringify(filtered));
  return true;
}

/** Process a single marker based on its sync status */
async function processSingleMarker(marker: LocalMarker, videoContentId: string): Promise<boolean> {
  switch (marker._syncStatus) {
    case "pending_create":
      return syncCreateMarker(marker, videoContentId);
    case "pending_update":
      return marker.id ? syncUpdateMarker(marker, videoContentId) : false;
    case "pending_delete":
      return marker.id ? syncDeleteMarker(marker, videoContentId) : false;
    default:
      return false;
  }
}

/** Log sync results */
function logSyncResults(synced: number, failed: number, videoContentId: string): void {
  if (synced > 0) {
    console.log(`[markerSync] ✅ ${synced} markers synced for ${videoContentId}`);
  }
  if (failed > 0) {
    console.warn(`[markerSync] ⚠️ ${failed} markers failed to sync for ${videoContentId}`);
  }
}

/** Sync all pending markers for a given video content */
export async function syncMarkers(videoContentId: string): Promise<{ synced: number; failed: number }> {
  const pending = getPendingMarkers(videoContentId);
  if (pending.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;

  for (const marker of pending) {
    try {
      const success = await processSingleMarker(marker, videoContentId);
      if (success) {
        synced++;
      } else {
        failed++;
      }
    } catch (e) {
      console.warn("[markerSync] network error for marker:", marker._localId, e);
      failed++;
    }
  }

  logSyncResults(synced, failed, videoContentId);
  return { synced, failed };
}

/** Sync all pending markers across all content ids */
export async function syncAllPendingMarkers(): Promise<{ synced: number; failed: number }> {
  const contentIds = getAllMarkerContentIds();
  let totalSynced = 0;
  let totalFailed = 0;

  for (const id of contentIds) {
    const result = await syncMarkers(id);
    totalSynced += result.synced;
    totalFailed += result.failed;
  }

  return { synced: totalSynced, failed: totalFailed };
}
